import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	OnInit,
	Signal,
	signal,
	WritableSignal
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { IContact } from "@calaosoft/osapp-common/contacts/models/IContact";
import { IGroup } from "@calaosoft/osapp-common/contacts/models/IGroup";
import { IGroupMember } from "@calaosoft/osapp-common/contacts/models/IGroupMember";
import { Contact } from "@calaosoft/osapp-common/contacts/models/contact";
import { EContactSelectionTab } from "@calaosoft/osapp-common/contacts/models/econtact-selection-tab";
import { Group } from "@calaosoft/osapp-common/contacts/models/group";
import { IParticipant } from "@calaosoft/osapp-common/conversations/models/iparticipant";
import { ObservableArray } from "@calaosoft/osapp-common/observable/models/observable-array";
import { secure } from "@calaosoft/osapp-common/rxjs/operators/secure";
import { ArrayHelper } from "@calaosoft/osapp-common/utils/helpers/arrayHelper";
import { IdHelper } from "@calaosoft/osapp-common/utils/helpers/idHelper";
import { StringHelper } from "@calaosoft/osapp-common/utils/helpers/stringHelper";
import { EPrefix } from "@calaosoft/osapp-common/utils/models/EPrefix";
import { combineLatest, defer, EMPTY, firstValueFrom, Observable, of } from "rxjs";
import { catchError, defaultIfEmpty, map, mergeMap, take, takeUntil, tap } from "rxjs/operators";
import { ComponentBase } from "../../../helpers/ComponentBase";
import { ConversationHelper } from "../../../helpers/ConversationHelper";
import { LifeCycleObserverComponentBase } from "../../../helpers/LifeCycleObserverComponentBase";
import { UserHelper } from "../../../helpers/user.helper";
import { EApplicationEventType } from "../../../model/application/EApplicationEventType";
import { IApplicationEvent } from "../../../model/application/IApplicationEvent";
import { EBarElementDock } from "../../../model/barElement/EBarElementDock";
import { EBarElementPosition } from "../../../model/barElement/EBarElementPosition";
import { EContactsType } from "../../../model/contacts/EContactsType";
import { EConversationType } from "../../../model/conversation/e-conversation-type";
import { ELifeCycleEvent } from "../../../model/lifeCycle/ELifeCycleEvent";
import { ILifeCycleEvent } from "../../../model/lifeCycle/ILifeCycleEvent";
import { ActivePageManager } from "../../../model/navigation/ActivePageManager";
import { EContactsPickerSort } from "../../../modules/contacts/components/contacts-picker-modal/econtacts-picker-sort";
import { IContactsPickerParams } from "../../../modules/contacts/models/contacts-picker/icontacts-picker-params";
import { Conversation } from "../../../modules/conversations/model/conversation";
import { ConversationComponentService } from "../../../modules/conversations/services/conversation-component.service";
import { ConversationConfigService } from "../../../modules/conversations/services/conversation-config.service";
import { GroupsUIService } from "../../../modules/groups/services/groups-ui.service";
import { PageManagerService } from "../../../modules/routing/services/pageManager.service";
import { ContactNamePipe } from "../../../pipes/contactName.pipe";
import { ContactsService } from "../../../services/contacts.service";
import { ConversationService } from "../../../services/conversation.service";
import { ShowMessageParamsPopup } from "../../../services/interfaces/ShowMessageParamsPopup";
import { UiMessageService } from "../../../services/uiMessage.service";
import { DynamicPageComponent } from "../../dynamicPage/dynamicPage.component";
import { ToolbarComponent } from "../../toolbar/toolbar.component";

interface IContactData {
	label: string;
	groups?: string;
	_id: string;
}

@Component({
	selector: "calao-conversation-edit",
	templateUrl: "./conversationEdit.component.html",
	styleUrls: ["./conversationEdit.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
	providers: [ConversationComponentService]
})
export class ConversationEditComponent extends LifeCycleObserverComponentBase implements OnInit {
	//#region FIELDS

	private static readonly C_LOG_ID = "CONV.EDIT.C::";

	/** Tableau des contacts et groupes sélectionnés comme participants pour la conversation. */
	private readonly maSelectedParticipants = new ObservableArray<Contact | Group>();
	private readonly moConversation$: Observable<Conversation>;

	/** Tableau des contacts et groupes avant les modifications. */
	private maContactsAndGroupsBefore: Array<Contact | Group> = [];
	/** Tableau des anciens contacts et groupes sélectionnés comme participants pour la conversation. */
	private maOldSelectedParticipants: Array<Contact | Group> = [];
	/** Titre de base de la conversation. */
	private msOldTitle = "";

	//#endregion

	//#region PROPERTIES

	/**	Identifiant du l'utilisateur courant. */
	public readonly userContactId: string = UserHelper.getUserContactId();
	public readonly isMailConversation$: Observable<boolean>;

	/** Listes des contacts sélectionnés de la conversation. */
	public contacts: Array<Contact> = [];
	/** Liste des groupes sélectionnés de la conversation. */
	public groups: Array<Group> = [];
	/** Titre de la conversation. */
	public title?: string;
	/** Données utilisées pour afficher les informations des contacts dans l'UI. */
	public uiContacts: Array<IContactData> = [];

	private mbIsValidated = false;
	/** `true` si le bouton de validation a été cliqué. */
	public get isValidated(): boolean {
		return this.mbIsValidated;
	}

	public get canSave(): boolean {
		return ArrayHelper.hasElements(this.contacts) || ArrayHelper.hasElements(this.groups);
	}

	private readonly moIsLoadingSignal: WritableSignal<boolean> = signal<boolean>(true);
	public readonly isLoadingSignal: Signal<boolean> = computed(() => this.moIsLoadingSignal());

	//#endregion

	//#region METHODS

	constructor(
		private readonly isvcContacts: ContactsService,
		private readonly isvcGroups: GroupsUIService,
		/** Service de conversation */
		private readonly isvcConversation: ConversationService,
		private readonly isvcPageManager: PageManagerService,
		private readonly ioRouter: Router,
		private readonly ioContactNamePipe: ContactNamePipe,
		private readonly isvcUiMessage: UiMessageService,
		private readonly isvcConversationComponent: ConversationComponentService,
		private readonly ioRoute: ActivatedRoute,
		private isvcConversationConfig: ConversationConfigService,
		poParentPage: DynamicPageComponent<ComponentBase>,
		poChangeDetectorRef: ChangeDetectorRef
	) {
		super(poParentPage, poChangeDetectorRef);

		this.initToolbar$().pipe(takeUntil(this.destroyed$)).subscribe();

		this.moConversation$ = this.getConversation$().pipe(secure(this));
		this.isMailConversation$ = this.moConversation$.pipe(
			map((poConversation: Conversation) => poConversation.conversationType === EConversationType.mail),
			secure(this)
		);
	}

	public ngOnInit(): void {
		this.moConversation$
			.pipe(
				take(1), // Flux d'un seul événement et on s'assure d'en avoir qu'un car le composant ne sait pas gérer les flux live.
				tap((poConversation: Conversation) => {
					if (StringHelper.isBlank(this.title)) this.msOldTitle = this.title = poConversation.title ?? "";
					else this.msOldTitle = poConversation.title ?? "";
				}),
				mergeMap((poConversation: Conversation) => {
					return this.loadContactsAndGroupsFromIds$(
						poConversation.participants.map(
							(poParticipant: IParticipant<IContact | IGroup>) => poParticipant.participantId
						) ?? []
					).pipe(
						mergeMap((_) => this.initContacts$()),
						tap((_) => this.initContactsAndGroupsArrays(poConversation)),
						mergeMap((_) => this.updateContactsData$()),
						tap((_) => this.moIsLoadingSignal.set(false))
					);
				}),
				takeUntil(this.destroyed$)
			)
			.subscribe();
	}

	protected override onLifeCycleEvent(poValue: IApplicationEvent): void {
		if (poValue.type === EApplicationEventType.LifeCycleEvent) {
			switch ((poValue as ILifeCycleEvent).data.value) {
				case ELifeCycleEvent.viewWillEnter:
					this.detectChanges();
					break;
				case ELifeCycleEvent.viewDidLeave:
					// On réinitialise le booléen pour savoir si le bouton de validation a été cliqué, au cas où la page n'est pas détruite et qu'on revient dessus.
					this.mbIsValidated = false;
					break;
			}
		}
	}

	private initToolbar$(): Observable<void> {
		return combineLatest([this.maSelectedParticipants.changes$, this.getParentToolbar$()]).pipe(
			map(([paSelectedParticipants, poToolbar]: [Array<IGroup | IContact>, ToolbarComponent]) => {
				poToolbar.init(
					[
						{
							id: "circle",
							component: "fabButton",
							dock: EBarElementDock.bottom,
							position: EBarElementPosition.right,
							background: "#1BC14D",
							icon: "checkmark",
							color: "#fff",
							onTap: () => this.createOrUpdateConversationAsync(),
							options: { isDisable: !paSelectedParticipants || paSelectedParticipants.length < 2 }
						}
					],
					this.getInstanceId()
				);
			})
		);
	}

	private getConversation$(): Observable<Conversation> {
		return this.isvcConversationComponent.getConversation$(new ActivePageManager(this, this.ioRouter)).pipe(
			catchError((poError) => {
				console.error(
					`${ConversationEditComponent.C_LOG_ID}Failed to get conversation ${IdHelper.buildId(EPrefix.conversation, this.ioRoute.snapshot.params.conversationId)} :`,
					poError
				);
				this.isvcUiMessage.showPopupMessage(
					new ShowMessageParamsPopup({
						header: "Erreur",
						message: "La conversation n'a pas été trouvée, elle ne peut donc pas être modifiée."
					})
				);
				return defer(() => this.isvcPageManager.goBack()).pipe(mergeMap(() => EMPTY));
			})
		);
	}

	private initContacts$(): Observable<void> {
		if (!ArrayHelper.hasElements(this.contacts)) {
			this.contacts = [];
			return of(undefined);
		} else {
			//On ajoute l'utilisateur courant à la liste des contacts s'il n'en fait pas déjà partie.
			return this.isvcContacts.getContact(this.userContactId).pipe(
				map((poCurrentUser?: Contact) => {
					if (poCurrentUser) {
						ArrayHelper.pushIfNotPresent(
							this.contacts,
							poCurrentUser,
							(poContact: Contact) => poContact._id === this.userContactId
						);
					}
				})
			);
		}
	}

	private initContactsAndGroupsArrays(poConversation?: Conversation): void {
		// On ajoute tous les groupes et on ajoute tous les contacts qui sont présents en tant que participant, les autres sont issus des groupes.
		this.maSelectedParticipants.resetArray([
			...this.groups,
			...this.contacts.filter((poContact: Contact) =>
				poConversation?.participants.some(
					(poParticipant: IParticipant<IGroup | IContact>) => poParticipant.participantId === poContact._id
				)
			)
		]);
		this.maOldSelectedParticipants = [...this.maSelectedParticipants];
		this.maContactsAndGroupsBefore = Array.from(this.maSelectedParticipants);
	}

	/** Met à jour les informations à afficher à l'utilisateur. Charge les contacts de tous les groupes sélectionnés. */
	private updateContactsData$(): Observable<Map<string, Contact[]>> {
		// Affiche tous les contacts sélectionnés.
		this.uiContacts = this.contacts.map((poContact: Contact) => this.createContactData(poContact));

		// Récupère les contacts des groupes sélectionnés.
		return this.isvcGroups.getGroupContacts(this.groups).pipe(
			tap((paGroupsContacts: Map<string, Contact[]>) => {
				this.groups.forEach((poGroup: Group) => {
					// Pour chaque groupe, on met à jour ses contacts.
					if (paGroupsContacts.has(poGroup._id)) {
						paGroupsContacts.get(poGroup._id)!.forEach((poContact: Contact) => {
							const lnIndex: number = this.uiContacts.findIndex(
								(poContactData: IContactData) => poContactData._id === poContact._id
							);

							if (lnIndex !== -1) {
								// On l'ajoute au contactData existant.
								this.uiContacts[lnIndex].groups = this.uiContacts[lnIndex].groups
									? `${this.uiContacts[lnIndex].groups}, ${poGroup.name}`
									: poGroup.name;
								// On créé un nouveau contactData.
							} else this.uiContacts.push(this.createContactData(poContact, poGroup));
						});
					}
				});

				this.detectChanges();
			})
		);
	}

	/** Retourne un objet `ContactData` par défaut */
	private createContactData(poContact: Contact, poGroup?: Group): IContactData {
		return {
			label: this.ioContactNamePipe.transform(poContact),
			groups: poGroup ? poGroup.name : undefined,
			_id: poContact._id
		};
	}

	/** Charge tous les groupes, les contacts, et les contacts des groupes. Les assigne à `this.contacts` et `this.groups`. */
	private loadContactsAndGroupsFromIds$(paParticipantsIds: Array<string>): Observable<[Group[], Contact[]]> {
		const laGroupsIds: string[] = [];
		const laContactsIds: string[] = [];

		// Dissocie les ids des groupes de ceux des contacts.
		paParticipantsIds.forEach((psId: string) => {
			if (ContactsService.isContact(psId)) laContactsIds.push(psId);
			else if (GroupsUIService.isGroup(psId)) laGroupsIds.push(psId);
			else console.error(`${ConversationEditComponent.C_LOG_ID}Type Inconnu :`, psId);
		});

		const loGetGroups$: Observable<Group[]> = this.isvcGroups
			.getDisplayableGroups(laGroupsIds)
			.pipe(tap((paGroups: Group[]) => (this.groups = paGroups)));
		const loGetContacts$: Observable<Contact[]> = this.getContacts$(laContactsIds, laGroupsIds);

		return combineLatest([loGetGroups$, loGetContacts$]);
	}

	/** Récupère et assigne les contacts (y compris les contacts des groupes). */
	private getContacts$(paContactsIds: string[], paGroupIds: string[]): Observable<Contact[]> {
		return this.isvcContacts.getContactsByIds(paContactsIds).pipe(
			tap((paContacts: Contact[]) => (this.contacts = paContacts)),
			mergeMap((_) => this.isvcGroups.getGroupContacts(paGroupIds)),
			map((poContactsByGroupId: Map<string, Contact[]>) => {
				paGroupIds.forEach((psGroupId: string) => this.contacts.push(...(poContactsByGroupId.get(psGroupId) ?? [])));
				return (this.contacts = ArrayHelper.unique(this.contacts, (poContact: Contact) => poContact._id));
			})
		);
	}

	/** Crée ou met à jour une conversation puis route vers la page précédente ou la conversation. */
	private async createOrUpdateConversationAsync(): Promise<void> {
		this.mbIsValidated = true;

		try {
			const loConversation: Conversation = await firstValueFrom(this.moConversation$);
			await firstValueFrom(this.updateConversation$(loConversation));
		} catch (poError) {
			this.mbIsValidated = false;
			console.error(
				`${ConversationEditComponent.C_LOG_ID}An error occurred while creating or updating conversation "${(await firstValueFrom(this.moConversation$))._id}".`,
				poError
			);
		}
	}

	private updateConversation$(poConversation: Conversation): Observable<void> {
		return defer(() => {
			poConversation.title = this.title?.trim();
			// Met à jour la conversation et route.

			return this.isvcConversation.updateConversation(
				poConversation,
				this.maSelectedParticipants,
				this.maContactsAndGroupsBefore
			);
		}).pipe(
			mergeMap((_) => {
				// Si on peut retourner en arrière on le fait, sinon on se déplace sur la conversation modifié.
				if (this.ioRouter.navigated)
					return this.isvcPageManager.goBack(); // Utile si on est une modale.
				// TODO La conversation sur laquelle on va router va avoir un bouton de retour qui pointera vers le mode edit.
				else return this.isvcConversation.routeToConversationFromConversationAsync(poConversation);
			})
		);
	}

	/** Affiche la modale de sélection des contacts et mets à jour l'UI après modification. */
	public async openContactSelectorAsync(): Promise<void> {
		// Récupération de la config du workspace
		const loConfig = await firstValueFrom(this.isvcConversationConfig.config$);

		const loContactsSelectorParams: IContactsPickerParams = {
			excludeCurrentUser: true,
			hasSearchbox: true,
			preSelectedIds: this.maSelectedParticipants.map((poMember: Contact | Group) => poMember._id),
			type: EContactsType.contactsAndGroups,
			disableItemFunction: (poContact: Contact) => !ConversationHelper.isParticipantEligible(poContact),
			hideAllSelectionButton: !loConfig?.participantsSelection?.selectAllEnabled,
			defaultTab: loConfig?.participantsSelection?.defaultTab ?? EContactSelectionTab.contacts,
			excludeUsers: loConfig?.participantsSelection?.excludeUsers,
			excludeNonUsers: loConfig?.participantsSelection?.excludeNonUsers,
			sort: EContactsPickerSort.byPreSelectedParticipants,
			min: 1
		};

		const laMembers: IGroupMember[] | undefined = await firstValueFrom(
			this.isvcContacts.openContactsSelectorAsModal(loContactsSelectorParams).pipe(defaultIfEmpty(undefined))
		);

		if (laMembers) {
			this.maSelectedParticipants.resetArray(laMembers as Array<Contact | Group>);
			this.contacts = laMembers.filter((poMember: IGroupMember) =>
				ContactsService.isContact(poMember._id)
			) as Contact[];
			this.groups = laMembers.filter((poMember: IGroupMember) => GroupsUIService.isGroup(poMember._id)) as Group[];

			// Récupère les contacts des groupes et des contacts à partir de leurs ids.
			await firstValueFrom(
				this.loadContactsAndGroupsFromIds$(laMembers.map((poValue: IContact | IGroup) => poValue._id))
			);
			await firstValueFrom(this.updateContactsData$());
		}
	}

	/** Supprime le groupe cliqué.
	 * @param poGroup Groupe à supprimer.
	 */
	public removeAsync(poGroup: IGroup): Promise<void>;
	/** Supprime le contact cliqué.
	 * @param poContactOrGroup Contact à supprimer.
	 */
	public removeAsync(poContact: IContactData): Promise<void>;
	public async removeAsync(poContactOrGroup: IContactData | IGroup): Promise<void> {
		ArrayHelper.removeElementByFinder(
			this.maSelectedParticipants,
			(poItem: Contact | Group) => poItem._id === poContactOrGroup._id
		);

		if (ContactsService.isContact(poContactOrGroup._id)) {
			if ((poContactOrGroup as IContactData).groups) {
				console.error(
					`${ConversationEditComponent.C_LOG_ID}On ne doit pas pouvoir supprimer un contact qui est dans un groupe.`
				);
			} else {
				ArrayHelper.removeElementById(this.contacts, poContactOrGroup._id);
				// Supprime le contact de l'interface utilisateur.
				this.uiContacts = this.uiContacts.filter(
					(poContactData: IContactData) => poContactData._id !== poContactOrGroup._id
				);
			}
		} else if (GroupsUIService.isGroup(poContactOrGroup._id)) {
			const laContactsGroups: Contact[] = await firstValueFrom(this.isvcGroups.getGroupContacts(poContactOrGroup._id));
			const laFilteredGroupContacts: Contact[] = laContactsGroups.filter((poContact: Contact) =>
				this.contacts.some((poItem: Contact) => poItem._id === poContact._id)
			);
			this.maSelectedParticipants.resetArray(
				ArrayHelper.unique(this.maSelectedParticipants.concat(laFilteredGroupContacts))
			);

			ArrayHelper.removeElementById(this.groups, poContactOrGroup._id);

			await firstValueFrom(this.updateContactsData$()); // Met à jour l'interface.
		} else {
			console.error(
				`${ConversationEditComponent.C_LOG_ID}Impossible de supprimer, objet non reconnu.`,
				poContactOrGroup
			);
		}

		this.detectChanges();
	}

	/** Retourne 'true' s'il y a eu des modifications, 'false' sinon. */
	public hasChanges(): boolean {
		const lbParticipantsChanged: boolean = !ArrayHelper.areArraysEqual(
			this.maOldSelectedParticipants,
			this.maSelectedParticipants,
			(poOldParticipant: Contact | Group, poNewParticipant: Contact | Group) =>
				poOldParticipant._id === poNewParticipant._id
		);
		const lbTitleChanged: boolean = this.msOldTitle !== this.title;

		return lbParticipantsChanged || lbTitleChanged;
	}

	//#endregion
}
